import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../lib/utils';

export interface DropdownItem {
  label: string;
  onClick: () => void;
}

interface DropdownProps {
  /** Content rendered inside the trigger button (icon, text, etc.) */
  trigger: React.ReactNode;
  /** Optional small heading shown at the top of the panel */
  title?: string;
  /** List of items to display in the panel */
  items: DropdownItem[];
  /** Extra classes applied to the trigger button */
  triggerClassName?: string;
  /** HTML title attribute (tooltip) on the trigger button */
  triggerTitle?: string;
  /** Which side the panel anchors to. Defaults to 'right'. */
  align?: 'left' | 'right';
}

export function Dropdown({
  trigger,
  title,
  items,
  triggerClassName,
  triggerTitle,
  align = 'right',
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Calculate position when opening
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuHeight = 8 + items.length * 42 + (title ? 32 : 0); // approx height
      const spaceBelow = window.innerHeight - rect.bottom - 4;
      const openUpward = spaceBelow < menuHeight && rect.top > menuHeight;
      setMenuPosition({
        top: openUpward ? rect.top - menuHeight : rect.bottom + 4,
        left: align === 'right' ? rect.right - 176 : rect.left, // 176 = w-44 (11rem)
      });
    }
  }, [open, align, items.length, title]);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const defaultTriggerClass = cn(
    "p-2 rounded-lg transition-colors",
    "bg-primary-brown/10 hover:bg-primary-brown/20 text-primary-brown"
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        onClick={() => setOpen(v => !v)}
        className={triggerClassName ?? defaultTriggerClass}
        title={triggerTitle}
      >
        {trigger}
      </button>

      {open && (
        <div
          className={cn(
            "fixed w-44 flex flex-col rounded-xl shadow-lg border",
            "border-slate-200/80"
          )}
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
            backgroundColor: 'var(--color-surface-light-pro, #e9eff4)',
            zIndex: 9999,
          }}
        >
          {title && (
            <div
              className={cn(
                "px-4 py-2 text-xs font-semibold border-b",
                "text-primary-brown/60 border-slate-200/80"
              )}
            >
              {title}
            </div>
          )}

          {items.map((item, i) => (
            <button
              key={i}
              onClick={() => { item.onClick(); setOpen(false); }}
              className={cn(
                "w-full text-left px-4 py-2.5 text-sm font-medium transition-colors",
                "text-text-primary hover:bg-primary-brown/8",
                i === 0 && "rounded-t-xl",
                i === items.length - 1 && "rounded-b-xl"
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
